<template>
  <v-dialog
    :value="value"
    max-width="540"
    @input="$emit('input', $event)"
  >
    <v-card>
      <button class="btn-close" @click="onClose">
        <v-icon class="icon-close">mdi-close</v-icon>
      </button>

      <div class="modal-header">
        <div class="warning-icon">
          <v-icon color="#EBA714" size="24">
            mdi-alert-circle-outline
          </v-icon>
        </div>

        <div class="header-headline">
          สาขาที่ทำรายการ Trade-in ไว้<br />
          ไม่ตรงกับสาขาที่เลือกในการสั่งซื้อ
        </div>
        <div class="header-subheadline">
          ต้องการเปลี่ยนเป็นสาขาที่ทำรายการ Trade-in ไว้หรือไม่
        </div>
      </div>

      <div class="modal-body">
        <div class="section-label">
          เลือกรายการ Trade-in ที่ต้องการใช้กับคำสั่งซื้อนี้
        </div>

        <BaseTradeInVoucherSelectionGroup
          v-model="selectedCodes"
          :options="vouchers"
        />

        <div class="section-label">
          เปลี่ยนเป็นสาขาที่ทำรายการ Trade-in
        </div>

        <div class="branch-switch">
          <div class="branch-switch-row">
            <div class="branch-box">
              <div class="branch-box-label">สาขาคำสั่งซื้อ</div>
              <div class="branch-box-value">
                {{ currentBranchName }}
              </div>
            </div>

            <v-icon size="20" color="rgba(0,0,0,0.45)">
              mdi-arrow-right
            </v-icon>

            <div class="branch-box">
              <div class="branch-box-label">
                สาขาที่ทำรายการ trade-in
              </div>
              <div class="branch-box-value">
                {{ selectedBranchName || '-' }}
              </div>
            </div>
          </div>

          <div class="pt-3">
            <BaseTradeInVoucherSummary
              :type="type"
              :totalPrice="totalPrice"
              :discount="discount"
              :tradeInDiscount="tradeInDiscount"
              :tradeInVoucherCount="selectedCodes.length"
              :finalPrice="finalPrice"
            />
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <v-btn
          depressed
          rounded
          block
          large
          class="btn-confirm-branch"
          :dark="canConfirm"
          :disabled="!canConfirm"
          color="primary"
          @click="onConfirm"
        >
          <span class="text-truncate-1">
            ใช่ เปลี่ยนเป็นสาขา {{ selectedBranchName }}
          </span>
        </v-btn>

        <v-btn
          outlined
          rounded
          block
          large
          color="#FF4252"
          @click="onDecline"
        >
          ไม่เปลี่ยนสาขา
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { TRADEIN_TYPE } from '@/config/order'
import BaseTradeInVoucherSelectionGroup from './BaseTradeInVoucherSelectionGroup.vue'
import BaseTradeInVoucherSummary from './BaseTradeInVoucherSummary.vue'

export default {
  name: 'BaseTradeInVoucherMismatchModal',

  components: {
    BaseTradeInVoucherSelectionGroup,
    BaseTradeInVoucherSummary,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: '',
    },
    vouchers: {
      type: Array,
      default: () => [],
    },
    currentBranchName: {
      type: String,
      default: null,
    },
    totalPrice: {
      type: Number,
      default: 0,
    },
    discount: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      TRADEIN_TYPE,
      selectedCodes: [],
    }
  },

  computed: {
    selectedVouchers() {
      return this.vouchers.filter(voucher =>
        this.selectedCodes.includes(voucher.code),
      )
    },

    selectedBranchCode() {
      return this.selectedVouchers[0]?.branch ?? null
    },

    selectedBranchName() {
      return this.selectedVouchers[0]?.branch_name ?? null
    },

    tradeInDiscount() {
      // If no vouchers are selected, calculate the total discount from all valid vouchers
      if (this.selectedVouchers.length === 0) {
        return this.vouchers
          .filter(voucher => voucher.valid)
          .reduce(
            (sum, voucher) => sum + Number(voucher.amount ?? 0),
            0,
          )
      }

      // If vouchers are selected, calculate the total discount from the selected vouchers
      return this.selectedVouchers.reduce(
        (sum, voucher) => sum + Number(voucher.amount ?? 0),
        0,
      )
    },

    finalPrice() {
      if (this.type === TRADEIN_TYPE.TRADEIN) {
        return Math.max(
          this.totalPrice - this.discount - this.tradeInDiscount,
          0,
        )
      } else {
        return this.totalPrice
      }
    },

    canConfirm() {
      return this.selectedCodes.length > 0
    },
  },

  watch: {
    value(isOpen) {
      if (isOpen) {
        this.selectedCodes = []
      }
    },
  },

  methods: {
    onClose() {
      this.$emit('input', false)
    },

    onDecline() {
      this.$emit('decline')
      this.$emit('input', false)
    },

    onConfirm() {
      if (!this.canConfirm) return

      this.$emit('confirm', {
        vouchers: this.selectedCodes,
        branch: this.selectedBranchCode,
      })
    },
  },
}
</script>

<style lang="stylus" scoped>
.modal-header {
    padding: 24px 24px 16px;

	display: flex;
	flex-direction: column;
    justify-content: center;
    align-items: center;
	gap: 8px;


    .warning-icon {
        display: flex;
        align-items: center;
        justify-content: center;

        width: 48px;
        height: 48px;
        border-radius: 50%;
        background-color: rgba(#EBA714, 0.12);
    }

    .header-headline {
        font-size: 18px;
        font-weight: 700;
        color: rgba(#000000, 0.85);
		text-align: center;
    }

    .header-subheadline {
        font-size: 14px;
        color: rgba(#000000, 0.45);
		text-align: center;
    }
}

.btn-close {
        position: absolute;
        top: 16px;
        right: 16px;

        width: 40px;
        height: 40px;
        background: #e8e8e8;
        border-radius: 50% !important;

        &:hover {
            .icon-close {
                color: rgba(0, 0, 0, 0.72);
            }
        }

        .icon-close {
            color: rgba(18, 18, 18, 0.56);
        }
}

.modal-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 24px 16px;

    .section-label {
        font-size: 14px;
        color: rgba(#000000, 0.65);
    }
}

.branch-switch {
	border: 1px solid #D0D0D0;
	border-radius: 8px;
	overflow: hidden;

    .branch-switch-label {
        margin-bottom: 8px;
        font-size: 14px;
        color: rgba(#000000, 0.45);
    }

    .branch-switch-row {
        display: flex;
        gap: 12px;

        padding: 12px;
		background-color: #F8F8FA;
    }

    .branch-box {
        flex: 1;
        min-width: 0;
		text-align: center;

        .branch-box-label {
            font-size: 12px;
            color: rgba(#000000, 0.45);
        }

        .branch-box-value {
            font-size: 14px;
            font-weight: 500;
            color: rgba(#000000, 0.85);
        }
    }
}

.modal-footer {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px 24px 24px;
}

.text-truncate-1 {
	display: -webkit-box;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	max-width: 100%;
}

::v-deep .btn-confirm-branch .v-btn__content {
	min-width: 0;
	max-width: 100%;
	overflow: hidden;
}
</style>
